import React from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ImportContactsIcon from '@mui/icons-material/ImportContacts'
import { getDocsHref } from '../../navigation'

// Standard signpost for pages that are empty on a fresh tenant. Explains what
// the page is for, how to get the first data in, and links to the page where
// that happens (plus an optional docs deep-link).
//
//   <EmptyState
//     icon={PolicyIcon}
//     title="No policies yet"
//     description="Policies uploaded here are..."
//     steps={['Open Site Configuration', 'Upload a document', '...']}
//     actions={[{ label: 'Open Site Configuration', to: '/site-configuration' }]}
//     docsHref="/docs/governance/policies"
//   />
//
// `actions[0]` renders contained, the rest outlined. Use `onClick` instead of
// `to` for same-page actions (e.g. opening an add-new modal).
//
// Instead of a hand-written docsHref, pass `registryId` (the page's id in
// src/navigation/registry.js) and the docs link is derived from the registry's
// docs.articleId - so it can't go stale separately from the help drawer.
const EmptyState = ({
  icon: Icon,
  title,
  description,
  steps,
  actions = [],
  docsHref,
  registryId,
  sx,
}) => {
  const navigate = useNavigate()
  const resolvedDocsHref = docsHref ?? getDocsHref(registryId)

  return (
    <Card
      variant="outlined"
      sx={{ p: { xs: 3, sm: 5 }, width: '100%', boxSizing: 'border-box', ...sx }}
    >
      <Stack spacing={2} alignItems="center" textAlign="center">
        {Icon && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(38, 142, 187, 0.1)',
            }}
          >
            <Icon sx={{ fontSize: 32, color: 'anMainBlue.main' }} />
          </Box>
        )}
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 560 }}
          >
            {description}
          </Typography>
        )}
        {steps?.length > 0 && (
          <Stack
            component="ol"
            spacing={0.75}
            sx={{ m: 0, pl: 0, listStylePosition: 'inside', maxWidth: 560 }}
          >
            {steps.map((step, index) => (
              <Typography
                key={index}
                component="li"
                variant="body2"
                color="text.secondary"
                textAlign="left"
              >
                {step}
              </Typography>
            ))}
          </Stack>
        )}
        {(actions.length > 0 || resolvedDocsHref) && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent="center"
            pt={0.5}
          >
            {actions.map(({ label, to, onClick, ...props }, index) => (
              <Button
                key={label}
                variant={index === 0 ? 'contained' : 'outlined'}
                onClick={onClick || (() => navigate(to))}
                {...props}
              >
                {label}
              </Button>
            ))}
            {resolvedDocsHref && (
              <Button
                variant="text"
                startIcon={<ImportContactsIcon />}
                onClick={() => navigate(resolvedDocsHref)}
              >
                How it works
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  )
}

export default EmptyState
